/* PDFCommunity - animated network in the hero (option B "data pulses").
   Purely decorative: a fixed network of gold dots with small light pulses
   travelling along the lines. It never touches the tools.
   v45: it reacts to your files. window.PDFC_NET.rush(el, ms) sends the
   pulses racing to an element (the tool card) and .flash() makes the whole
   network light up once (download ready).
   - stops when the hero is out of view or the tab is hidden
   - "reduce motion" in the OS: one still frame, no animation
   - fewer dots on small screens
   - (v52) the idle animation stops by itself after a few seconds (WCAG
     2.2.2: nothing moves for longer than 5 s without a way to stop it);
     rush() and flash() still play, they answer the visitor's own action
   - (v88) on a phone the idle network stands still (one drawn frame): its
     frames cost a slow phone about a second of work in the first seconds
   - (v99) lines and pulses fade out behind the hero text (title, intro,
     badges): they crossed the words and made them harder to read
   - (v141) the faded text area is a CSS mask made once per build (was ~30
     compositing fills per frame) and the calm motion draws 30 frames a second,
     time-scaled so it moves at the same speed: on a computer without graphics
     acceleration the animation took nearly the whole main thread (Lighthouse
     desktop: hero 17 s CPU, TBT 1.5 s -> 1 s, 0-40 ms)
   - (v114) the racing pulses fly to the outside of the gold ring and their tail
     follows the head in, instead of the whole pulse vanishing under the ring
   - (v106) the race thins out over 1.5 s when it ends (release / leave), no more abrupt stop
   - (v105) a rush no longer cuts the calm motion short: it keeps going 8 s after the race
   - (v104) the calm motion runs 20 s (IDLE_STOP_MS), then stops and the
     button flips to "play"; the rush after a file arrives lasts 4 s
   - (v101) calm continuous motion again (desktop): the dots drift slowly and
     the pulses keep travelling. WCAG 2.2.2 is met by a pause button in the
     hero (PDFC_NET.setPaused, the button itself is made in pc-interact.js
     with the page's own language); "reduce motion" and phones stay still
   - (v100) the racing pulses end on the card's edge (its rounded outline)
     and light it up there: they used to aim at the card's centre and
     vanished behind its opaque face, so they never reached the gold edge */
(function () {
  'use strict';
  var hero = document.querySelector('.hero');
  if (!hero || !window.requestAnimationFrame) return;
  var cv = document.createElement('canvas');
  cv.className = 'hero-canvas';
  cv.setAttribute('aria-hidden', 'true');
  hero.insertBefore(cv, hero.firstChild);
  var ctx = cv.getContext('2d');
  if (!ctx) return;

  var mq = window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
  var reduce = !!(mq && mq.matches);
  var IDLE_STOP_MS = 20000; // (v104) the calm background motion stops by itself after this long (play button restarts it)
  var CALM_AFTER_RUSH_MS = 8000; // (v105) after a rush the dots keep drifting this long, so nothing freezes the moment the race ends
  var RUSH_TAPER_MS = 1500; // (v106) the race thins out over this time before it ends, instead of cutting off
  var DRIFT_PX = 9, DRIFT_MIN_S = 14, DRIFT_SPAN_S = 12; // (v101) how far a dot wanders and how long one slow loop takes
  var PHONE = !!(window.matchMedia && window.matchMedia('(max-width: 820px)').matches);
  function idleMs() { return PHONE ? 0 : IDLE_STOP_MS; }
  var idleUntil = 0; // set once the page has loaded (startAfterLoad)
  var paused = false, autoPaused = false, clock = 0, lastStep = 0; // (v101) paused by the visitor; the drift's own clock (runs only while frames are drawn)
  var W = 0, H = 0, nodes = [], edges = [], pulses = [], visible = true, running = false;
  // (v99) the text column of the hero; its box (hero coordinates) is erased from every frame
  var textEl = hero.querySelector('.hero-grid > :not(.tool-card)'), textBoxes = [];
  var REBUILD_DELAY_MS = 250; // (v139) see onResize
  var TEXT_PAD = 12, FEATHER_PX = 36, FEATHER_STEPS = 6, HERO_MAX_PX = 8e6;
  // (v141) the faded text area is a CSS mask, made once per build, instead of
  // ~30 compositing fills in every frame (half of each frame's work on a
  // computer without graphics acceleration). MASK_SCALE: the fade is soft, so
  // a half-resolution mask looks the same and encodes 4x faster.
  var MASK_SCALE = 0.5;
  var MIN_MASK_URL = 32; // (v142) a real PNG data URL is far longer than "data:,"
  var maskOn = false;
  var CSS_MASK = !!(window.CSS && CSS.supports && (CSS.supports('mask-image', 'url(x.png)') || CSS.supports('-webkit-mask-image', 'url(x.png)')));
  // (v141) the calm motion draws at most 30 frames a second (it drifts slowly); a rush or flash keeps every frame.
  // Movement is scaled by the real time between frames, so the speed is the same at any frame rate.
  var CALM_FRAME_MS = 1000 / 30, BASE_FRAME_MS = 1000 / 60, MAX_STEP = 3, lastDraw = 0, stepK = 1;
  var rushEl = null, rushUntil = 0, glow = 0, edgeGlow = 0;
  // (v114) a pulse is a head and a tail of dots, each TAIL_GAP of the way behind the one before
  var TAIL_DOTS = 6, TAIL_GAP = 0.025, TAIL_K = (TAIL_DOTS - 1) * TAIL_GAP;
  var RING_PX = 3; // the gold ring around the card (.tool-card.pc-glow box-shadow spread, and drawEdgeGlow)

  function gold(a) { return 'rgba(217,164,65,' + a + ')'; }
  // (v141) a per-frame chance at 60 fps, for a step of stepK frames
  function chance(p) { return 1 - Math.pow(1 - p, stepK); }
  function rng(seed) { return function () { seed = (seed * 16807) % 2147483647; return (seed - 1) / 2147483646; }; }

  function build() {
    var r = hero.getBoundingClientRect(), dpr = Math.min(window.devicePixelRatio || 1, 2);
    if (!r.width || !r.height) return;
    // (v111) a long file list makes the hero tall: stay well under the canvas limit (iPhone 16.7 MP)
    dpr = Math.min(dpr, Math.sqrt(HERO_MAX_PX / (r.width * r.height)));
    W = r.width; H = r.height;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    // (v139) fixed CSS size: between a resize and the delayed rebuild the old drawing keeps its shape (not stretched)
    cv.style.width = W + 'px'; cv.style.height = H + 'px';
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    var rand = rng(4242), n = Math.max(12, Math.min(38, Math.round(W * H / 26000)));
    nodes = [];
    for (var i = 0; i < n; i++) {
      var bx = 10 + DRIFT_PX + rand() * (W - 20 - 2 * DRIFT_PX), by = 10 + DRIFT_PX + rand() * (H - 20 - 2 * DRIFT_PX);
      nodes.push({ x: bx, y: by, bx: bx, by: by, r: 2 + rand() * 3, flash: 0,
        ax: DRIFT_PX * (0.5 + 0.5 * rand()), ay: DRIFT_PX * (0.5 + 0.5 * rand()),
        wx: 2 * Math.PI / (DRIFT_MIN_S + DRIFT_SPAN_S * rand()), wy: 2 * Math.PI / (DRIFT_MIN_S + DRIFT_SPAN_S * rand()),
        px: 2 * Math.PI * rand(), py: 2 * Math.PI * rand() });
    }
    var seen = {}; edges = [];
    nodes.forEach(function (a, i) {
      var near = nodes.map(function (b, j) { return [j, Math.hypot(a.x - b.x, a.y - b.y)]; })
        .filter(function (p) { return p[0] !== i; }).sort(function (p, q) { return p[1] - q[1]; }).slice(0, 2);
      near.forEach(function (p) {
        var lo = Math.min(i, p[0]), hi = Math.max(i, p[0]), k = lo + '-' + hi;
        if (!seen[k]) { seen[k] = 1; edges.push([lo, hi]); }
      });
    });
    textBoxes = measureText(r);
    maskOn = CSS_MASK && setMask();
    pulses = [];
    draw(false);
  }

  // one box per text block, so the lines stay visible next to a short
  // title or a narrow intro; a row of badges counts badge by badge
  function textBlocks() {
    var out = [];
    if (!textEl) return out;
    Array.prototype.forEach.call(textEl.children, function (el) {
      if (el.classList.contains('trust-row')) Array.prototype.push.apply(out, el.children);
      else out.push(el);
    });
    return out;
  }
  function measureText(heroRect) {
    return textBlocks().map(function (el) { return el.getBoundingClientRect(); })
      .filter(function (t) { return t.width && t.height; })
      .map(function (t) {
        return { x: t.left - heroRect.left - TEXT_PAD, y: t.top - heroRect.top - TEXT_PAD,
          w: t.width + 2 * TEXT_PAD, h: t.height + 2 * TEXT_PAD };
      });
  }

  // (v141) the same soft-edged erase as clearText, once, into a mask image for the canvas
  // (v142) false when no mask could be made (no 2d context, or an empty "data:," image, which as a
  // mask would hide the whole canvas): then the text is erased in every frame, as without CSS masks
  function setMask() {
    var m = document.createElement('canvas');
    m.width = Math.max(1, Math.round(W * MASK_SCALE)); m.height = Math.max(1, Math.round(H * MASK_SCALE));
    var mc = m.getContext('2d');
    var url = '';
    if (mc) {
      mc.scale(MASK_SCALE, MASK_SCALE);
      mc.fillStyle = '#000'; mc.fillRect(0, 0, W, H); // opaque = the network shows
      eraseText(mc);
      url = m.toDataURL('image/png');
    }
    var ok = url.length > MIN_MASK_URL;
    var v = ok ? 'url(' + url + ')' : '';
    cv.style.maskImage = v; cv.style.webkitMaskImage = v;
    cv.style.maskSize = ok ? '100% 100%' : ''; cv.style.webkitMaskSize = ok ? '100% 100%' : '';
    return ok;
  }
  // erase the text box with a soft edge: rings from the outside in, each
  // removing a bit more, then the box itself completely
  function eraseText(c) {
    if (!textBoxes.length) return;
    c.save();
    c.globalCompositeOperation = 'destination-out';
    c.fillStyle = 'rgba(0,0,0,0.3)';
    textBoxes.forEach(function (b) {
      for (var i = 0; i < FEATHER_STEPS; i++) {
        var g = FEATHER_PX * (1 - i / FEATHER_STEPS);
        c.fillRect(b.x - g, b.y - g, b.w + 2 * g, b.h + 2 * g);
      }
    });
    c.fillStyle = '#000';
    textBoxes.forEach(function (b) { c.fillRect(b.x, b.y, b.w, b.h); });
    c.restore();
  }
  function clearText() { if (!maskOn) eraseText(ctx); } // without a CSS mask: erase in every frame, as before

  // (v100) the rush target's rounded box in hero coordinates (l, t, rt, b, corner radius r)
  function target() {
    if (!rushEl) return null;
    var h = hero.getBoundingClientRect(), e = rushEl.getBoundingClientRect();
    var r = Math.min(parseFloat(getComputedStyle(rushEl).borderTopLeftRadius) || 0, e.width / 2, e.height / 2);
    return { l: e.left - h.left, t: e.top - h.top, rt: e.right - h.left, b: e.bottom - h.top, r: r };
  }
  // (v114) the pulses fly to the OUTSIDE of the gold ring: the card (with its ring) lies above this canvas,
  // so a pulse aimed at the card's own edge vanished under the ring before it seemed to reach it
  function ringBox(b) { return b && { l: b.l - RING_PX, t: b.t - RING_PX, rt: b.rt + RING_PX, b: b.b + RING_PX, r: b.r + RING_PX }; }
  function inside(box, x, y) { return x > box.l && x < box.rt && y > box.t && y < box.b; }
  // the point on the box's rounded outline nearest to (x, y), which lies outside the box
  function edgePoint(box, x, y) {
    var ix = Math.max(box.l + box.r, Math.min(box.rt - box.r, x)), iy = Math.max(box.t + box.r, Math.min(box.b - box.r, y));
    var dx = x - ix, dy = y - iy, d = Math.hypot(dx, dy);
    if (!d) return { x: x, y: y };
    if (box.r) return { x: ix + dx / d * box.r, y: iy + dy / d * box.r };
    return { x: Math.max(box.l, Math.min(box.rt, x)), y: Math.max(box.t, Math.min(box.b, y)) };
  }
  function drawEdgeGlow(box) {
    ctx.strokeStyle = gold(0.9 * edgeGlow); ctx.lineWidth = 3;
    ctx.beginPath();
    if (ctx.roundRect) ctx.roundRect(box.l - 1.5, box.t - 1.5, box.rt - box.l + 3, box.b - box.t + 3, box.r + 1.5);
    else ctx.rect(box.l - 1.5, box.t - 1.5, box.rt - box.l + 3, box.b - box.t + 3);
    ctx.stroke();
  }

  // (v101) every dot wanders slowly around its place (slow sine loops)
  function drift() {
    var now = Date.now();
    clock += lastStep ? Math.min(now - lastStep, 100) / 1000 : 0;
    lastStep = now;
    nodes.forEach(function (n) { n.x = n.bx + n.ax * Math.sin(clock * n.wx + n.px); n.y = n.by + n.ay * Math.sin(clock * n.wy + n.py); });
  }

  function draw(step) {
    if (step) drift();
    ctx.clearRect(0, 0, W, H);
    ctx.lineWidth = 1.4;
    ctx.strokeStyle = gold(0.22 + 0.45 * glow);
    edges.forEach(function (e) {
      ctx.beginPath(); ctx.moveTo(nodes[e[0]].x, nodes[e[0]].y); ctx.lineTo(nodes[e[1]].x, nodes[e[1]].y); ctx.stroke();
    });
    var now = Date.now(), rushing = rushEl && now < rushUntil;
    var tp = rushEl ? target() : null, ring = ringBox(tp); // (v139) measured once per frame (was twice: two forced layouts)
    if (step) {
      if (rushing) {
        var t = ring;
        // (v59) no nodes yet: nothing to send. (v100) only nodes outside the card send: one behind it has no edge to fly to
        var from = t ? nodes.filter(function (q) { return !inside(t, q.x, q.y); }) : [];
        if (from.length && Math.random() < chance(0.55 * Math.min(1, (rushUntil - now) / RUSH_TAPER_MS))) {
          var n = from[Math.floor(Math.random() * from.length)];
          pulses.push({ ax: n.x, ay: n.y, tx: true, k: 0, sp: 0.018 + Math.random() * 0.014 });
        }
      } else {
        var max = W < 700 ? 5 : 10;
        if (pulses.length < max && Math.random() < chance(0.06) && edges.length) {
          var e = edges[Math.floor(Math.random() * edges.length)], fwd = Math.random() < 0.5;
          pulses.push({ a: fwd ? e[0] : e[1], b: fwd ? e[1] : e[0], k: 0, sp: 0.006 + Math.random() * 0.006 });
        }
      }
    }
    pulses = pulses.filter(function (p) {
      if (step) p.k += p.sp * stepK;
      var ax, ay, bx, by;
      if (p.tx) { if (!ring) return false; ax = p.ax; ay = p.ay; var ep = edgePoint(ring, ax, ay); bx = ep.x; by = ep.y; }
      else { ax = nodes[p.a].x; ay = nodes[p.a].y; bx = nodes[p.b].x; by = nodes[p.b].y; }
      // (v114) the head lights the edge (or the dot) when it arrives; the tail keeps flying in after it.
      // It used to disappear with the head, so every pulse seemed to stop short of the gold edge.
      if (p.k >= 1 && !p.hit) { p.hit = true; if (p.tx) edgeGlow = 1; else nodes[p.b].flash = 1; }
      if (p.k - TAIL_K >= 1) return false;
      for (var s = 0; s < TAIL_DOTS; s++) {
        var k = Math.max(0, p.k - s * TAIL_GAP);
        if (k >= 1) continue; // this dot has arrived
        ctx.fillStyle = gold(0.85 * (1 - s / TAIL_DOTS));
        ctx.beginPath(); ctx.arc(ax + (bx - ax) * k, ay + (by - ay) * k, 3.2 - s * 0.4, 0, 7); ctx.fill();
      }
      return true;
    });
    nodes.forEach(function (p) {
      var f = Math.max(p.flash, glow);
      if (f > 0) {
        ctx.fillStyle = gold(0.25 * f);
        ctx.beginPath(); ctx.arc(p.x, p.y, p.r + 12 * f, 0, 7); ctx.fill();
      }
      if (step && p.flash > 0) p.flash = Math.max(0, p.flash - 0.025 * stepK);
      ctx.fillStyle = gold(0.6 + 0.4 * f);
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r, 0, 7); ctx.fill();
    });
    clearText();
    if (edgeGlow > 0 && tp) drawEdgeGlow(tp);
    if (step && edgeGlow > 0) edgeGlow = Math.max(0, edgeGlow - 0.05 * stepK);
    if (step && glow > 0) glow = Math.max(0, glow - 0.015 * stepK);
    if (!rushing && rushEl && edgeGlow <= 0 && !pulses.some(function (p) { return p.tx; })) rushEl = null;
  }

  function busy() { return (rushEl && Date.now() < rushUntil) || glow > 0 || edgeGlow > 0 || pulses.some(function (p) { return p.tx; }); }
  function loop() {
    if (!visible || document.hidden || reduce) { running = false; return; }
    if ((Date.now() > idleUntil || paused) && !busy()) {
      running = false;
      if (!paused) { paused = true; autoPaused = true; window.dispatchEvent(new Event('pdfc-net-state')); } // (v104) idle time is over: the button now offers "play"
      return;
    }
    var now = Date.now(), calm = !busy();
    if (calm && lastDraw && now - lastDraw < CALM_FRAME_MS - 2) { requestAnimationFrame(loop); return; }
    stepK = lastDraw ? Math.min(MAX_STEP, Math.max(1, (now - lastDraw) / BASE_FRAME_MS)) : 1;
    lastDraw = now;
    draw(true);
    requestAnimationFrame(loop);
  }
  var loaded = false; // (v58) see startAfterLoad below
  function start() { if (!running && visible && !document.hidden && !reduce && (loaded || busy()) && (!paused || busy())) { running = true; lastStep = 0; lastDraw = 0; requestAnimationFrame(loop); } }

  window.PDFC_NET = {
    rush: function (el, ms) { if (reduce || !el) return; rushEl = el; rushUntil = Date.now() + (ms || 1200);
      idleUntil = Math.max(idleUntil, rushUntil + CALM_AFTER_RUSH_MS); // (v105) was: idleUntil = rushUntil, which froze everything when the race ended
      if (autoPaused) { paused = false; autoPaused = false; window.dispatchEvent(new Event('pdfc-net-state')); }
      start(); },
    stop: function () { rushUntil = Math.min(rushUntil, Date.now() + RUSH_TAPER_MS); }, // (v106) fades out, was: rushUntil = 0
    flash: function () { if (reduce) return; glow = 1; start(); },
    // (v101) the visitor's pause / play for the calm background motion (WCAG 2.2.2)
    setPaused: function (p) { paused = !!p; autoPaused = false; if (!paused) { idleUntil = Date.now() + idleMs(); start(); } },
    isPaused: function () { return paused; },
    // false when nothing moves by itself (reduced motion, phone): no pause button is needed
    movable: !reduce && !PHONE
  };

  // (v59) with a ResizeObserver the first build() comes from its first
  // callback, right after the browser's own layout: measuring the hero
  // straight away forced an extra layout while the page was starting up
  // (v139) later resizes (a tab switch or a file list changes the hero's height) rebuild after the size has
  // settled: a rebuild re-allocates the whole canvas, and inside the observer it landed in the very frame that
  // answers the click (INP +100..150 ms on a phone). Meanwhile the canvas keeps its old pixel size (see build).
  var rebuildTimer = 0;
  function onResize() {
    if (!W) { build(); return; }
    clearTimeout(rebuildTimer);
    rebuildTimer = setTimeout(build, REBUILD_DELAY_MS);
  }
  if (window.ResizeObserver) new ResizeObserver(onResize).observe(hero);
  else { build(); window.addEventListener('resize', build); }
  if (window.IntersectionObserver) new IntersectionObserver(function (es) { visible = es[0].isIntersecting; start(); }).observe(hero);
  document.addEventListener('visibilitychange', start);
  // (v52) the OS setting may change while the page is open
  if (mq) {
    var onChange = function (e) { reduce = e.matches; if (reduce) draw(false); else { idleUntil = Date.now() + idleMs(); start(); } };
    if (mq.addEventListener) mq.addEventListener('change', onChange); else if (mq.addListener) mq.addListener(onChange);
  }
  // (v58) the still network is drawn at once (build above); the pulses only
  // start moving once the page has loaded, so their frames don't compete
  // with the page's own start-up on a slow phone
  function startAfterLoad() { loaded = true; idleUntil = Date.now() + idleMs(); if (idleMs()) start(); }
  if (document.readyState === 'complete') startAfterLoad();
  else window.addEventListener('load', startAfterLoad, { once: true });
})();
